"use client";

import { useState } from "react";
import { signupUser } from "@capex/services/apiQueries/user";
import { SignupData } from "@capex/types";
import { Checkbox, Spinner, Input } from "@material-tailwind/react";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { GenericButton } from "../general/button.component";
import validator from "validator";
import { PageFrameComponent } from "../general/page-frame.component";
import { lookupBvn } from "@capex/services/apiQueries/bvnValidation";
import { EyeOff, Eye } from "lucide-react";

export const Form1 = ({
  setStage,
  setForm1Data,
}: {
  setStage: (stage: number) => void;
  setForm1Data: (data: SignupData) => void;
}) => {
  const [isPending, startTransition] = useTransition();
  const [showBvn, setShowBvn] = useState(false);
  const [showNin, setShowNin] = useState(false);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNo: "",
      bvn: "",
      nin: "",
      dob: "",
      TandC: false,
    },
  });
  const millisecondsInEighteenYears = 1000 * 60 * 60 * 24 * 365.25 * 18; // (considering leap years)
  const Timestamp18YearsAgo =
    new Date().getTime() - millisecondsInEighteenYears;

  const onSubmit = handleSubmit(async (data) => {
    startTransition(async () => {
      try {
        const bvnRes = await lookupBvn(data.bvn);
        if (bvnRes.status === "successful") {
          setForm1Data({
            ...data,
            dateOfBirth: data.dob,
          });
          const signupRes = await signupUser({
            ...data,
            dateOfBirth: new Date(data.dob).toLocaleDateString("en-US"),
          });
          if (signupRes.statusCode === "Created") {
            setStage(2);
          } else if (
            signupRes.statusMessage ===
            "Client with email or phone number provided already exist!"
          ) {
            toast.error("User already exists. Sign in instead.");
          } else {
            toast.error("An error occurred");
          }
        } else {
          setError("bvn", {
            type: "pattern",
            message: "BVN is invalid",
          });
        }
      } catch (error) {
        toast.error("An unexpected error occurred.");
      }
    });
  });

  return (
    <PageFrameComponent>
      <div className="flex flex-col items-center gap-8 w-full">
        <p className="font-medium text-2xl md:text-3xl text-secondary">
          Personal Information
        </p>
        <form
          onSubmit={onSubmit}
          className="flex flex-col items-center gap-8 w-full"
        >
          <div className="flex flex-col gap-[18px] w-full md:w-[545px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-[18px] w-full">
              <div className="flex flex-col gap-1">
                <Input
                  variant="static"
                  color="black"
                  placeholder="First Name"
                  {...register("firstName", {
                    required: true,
                    minLength: 2,
                    maxLength: 40,
                  })}
                  {...({} as any)}
                />
                {errors?.firstName && (
                  <p className="text-red-400 font-medium text-sm">
                    {errors?.firstName?.type === "minLength" &&
                      "First Name must contain at least 2 characters"}
                    {errors.firstName?.type === "maxLength" &&
                      "First Name must contain maximum of 40 characters"}
                    {errors.firstName?.type === "required" &&
                      "First Name is required"}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Input
                  variant="static"
                  color="black"
                  placeholder="Surname"
                  {...register("lastName", {
                    required: true,
                    minLength: 2,
                    maxLength: 40,
                  })}
                  {...({} as any)}
                />
                {errors?.lastName && (
                  <p className="text-red-400 font-medium text-sm">
                    {errors?.lastName?.type === "minLength" &&
                      "Surname must contain at least 2 characters"}
                    {errors.lastName?.type === "maxLength" &&
                      "Surname must contain maximum of 40 characters"}
                    {errors.lastName?.type === "required" &&
                      "Surname is required"}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Input
                variant="static"
                color="black"
                placeholder="Email"
                {...register("email", {
                  required: true,
                  validate: (value) => validator.isEmail(value),
                })}
                {...({} as any)}
              />
              {errors?.email && (
                <p className="text-red-400 font-medium text-sm">
                  {errors.email?.type === "required" && "Email is required"}
                  {errors.email?.type === "validate" && "Email must be valid"}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Input
                variant="static"
                color="black"
                placeholder="Phone Number"
                {...register("phoneNo", {
                  required: true,
                  validate: (value) => validator.isMobilePhone(value),
                })}
                {...({} as any)}
              />
              {errors?.phoneNo && (
                <p className="text-red-400 font-medium text-sm">
                  {errors.phoneNo?.type === "required" &&
                    "Phone Number is required"}
                  {errors.phoneNo?.type === "validate" &&
                    "Phone Number must be valid"}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1 relative">
              <Input
                type={showBvn ? "text" : "password"}
                variant="static"
                color="black"
                placeholder="BVN"
                inputMode="numeric"
                pattern="[0-9]*"
                {...register("bvn", {
                  required: true,
                  minLength: 11,
                  maxLength: 11,
                  validate: (value) => validator.isNumeric(value),
                  onChange: (e) => {
                    // Sanitize input: remove any non-digit characters
                    e.target.value = e.target.value.replace(/\D/g, "");
                  },
                })}
                {...({} as any)}
              />
              <button
                type="button"
                onClick={() => setShowBvn(!showBvn)}
                className="absolute top-4 right-1 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={showBvn ? "Hide BVN" : "Show BVN"}
              >
                {showBvn ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {errors?.bvn && (
                <p className="text-red-400 font-medium text-sm">
                  {errors?.bvn?.type === "minLength" &&
                    "BVN must contain 11 digits"}
                  {errors.bvn?.type === "maxLength" &&
                    "BVN must contain 11 digits"}
                  {errors.bvn?.type === "required" && "BVN is required"}
                  {errors.bvn?.type === "validate" &&
                    "BVN characters must be digits"}
                  {errors.bvn?.type === "pattern" && "BVN is invalid"}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1 relative">
              <Input
                type={showNin ? "text" : "password"}
                variant="static"
                color="black"
                placeholder="NIN"
                inputMode="numeric"
                pattern="[0-9]*"
                {...register("nin", {
                  required: true,
                  minLength: 11,
                  maxLength: 11,
                  validate: (value) => validator.isNumeric(value),
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                  },
                })}
                {...({} as any)}
              />
              <button
                type="button"
                onClick={() => setShowNin(!showNin)}
                className="absolute top-4 right-1 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={showNin ? "Hide NIN" : "Show NIN"}
              >
                {showNin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {errors?.nin && (
                <p className="text-red-400 font-medium text-sm">
                  {errors?.nin?.type === "minLength" &&
                    "NIN must contain 11 digits"}
                  {errors.nin?.type === "maxLength" &&
                    "NIN must contain 11 digits"}
                  {errors.nin?.type === "required" && "NIN is required"}
                  {errors.nin?.type === "validate" &&
                    "NIN characters must be digits"}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Input
                variant="static"
                label="Date of birth (above 18)"
                labelProps={{
                  className: "!text-text-placeholder !text-xs mt-2",
                }}
                placeholder="dd/mm/yyyy"
                className="text-text-dark"
                color="black"
                type="date"
                {...register("dob", {
                  required: true,
                  validate: (value) =>
                    //@ts-ignore
                    new Date(value) !== "Invalid Date" &&
                    new Date(value).getTime() < Timestamp18YearsAgo,
                })}
                {...({} as any)}
              />
              {errors?.dob && (
                <p className="text-red-400 font-medium text-sm">
                  {errors.dob?.type === "validate" &&
                    "Date of Birth must be a validate date and you must be above 18 years"}
                  {errors.dob?.type === "required" &&
                    "Date of Birth is required"}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 justify-start w-full md:w-[545px]">
            <Checkbox
              className="-m-3 border-disabled"
              label={
                <p className="flex flex-wrap gap-[5px] text-[15px] text-text-darker ml-2">
                  I agree with the
                  <a
                    href="https://capitalexpressassurance.com/terms-and-conditions/"
                    target="_blank"
                    className="text-text-links font-medium transition-colors hover:opacity-80"
                  >
                    terms and conditions
                  </a>
                  and
                  <a
                    href="https://capitalexpressassurance.com/privacy-policy/"
                    target="_blank"
                    className="text-text-links font-medium transition-colors hover:opacity-80"
                  >
                    data protection policy.
                  </a>
                </p>
              }
              {...register("TandC", {
                validate: (value) => value === true,
              })}
              {...({} as any)}
            />
            {errors?.TandC && (
              <p className="text-red-400 font-medium text-sm">
                You must agree to terms and conditions and data protection
                policy to proceed
              </p>
            )}
          </div>

          <div className="w-full md:w-[450px] mx-auto mt-3">
            <GenericButton type="submit" className="bg-primary !w-full">
              {isPending ? (
                <div className="flex justify-center items-center">
                  <Spinner className="h-6 w-6" {...({} as any)} />
                </div>
              ) : (
                "Continue"
              )}
            </GenericButton>
          </div>
        </form>
      </div>
    </PageFrameComponent>
  );
};
